<template>
  <div>
    <div
      class="s-meta__type">
      <template v-if="attachmentType === 'image'">
        <ImagePreview
          class="u-marginBottom5"
          v-bind="imagePreviewProps"
          @click="openImage"
          @cancel="handleCancelImage" />
      </template>
      <template v-else-if="attachmentType === 'audio'">
        <AudioPlayer
          v-if="volumes && volumes.length"
          :volumes="volumes"
          :audio-url="message.content"
          :duration-seconds="audioDurationSeconds" />
        <audio
          v-else
          draggable="false"
          controls
          class="s-chat__audio u-marginBottom2"
          controlsList="nodownload">
          <source :src="message.content" />
        </audio>
      </template>
      <template v-else-if="attachmentType === 'video'">
        <video
          draggable="false"
          controls
          class="s-chat__video u-marginBottom2"
          controlsList="nodownload">
          <source :src="message.content" />
        </video>
      </template>
      <template v-else>
        <a
          draggable="false"
          :href="message.content"
          download
          target="_blank"
          rel="noopener noreferrer"
          class="s-chat__messageDownload">
          <span class="s-messageAttachment__icon">
            <SvgIcon
              class="-mediumWithBackground"
              :type="`filetype_${attachmentType}`" />
          </span>
          <span>
            <span
              v-safe-chat-html="props.message.metadata?.file_name"
              class="s-messageAttachment__fileName" />
            <span class="t-caption2 u-colorDarkMediumEmphasis s-d-block">{{ size | kb2human }}</span>
          </span>
        </a>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import AudioPlayer from '@/components/AudioPlayer.vue'
import constants from '@/constants'
import { useChatAttachmentsStore } from '@/modules/chat/chatAttachmentsStore'
import { useChatStore } from '@/modules/chat/chatStore'
import ImagePreview from '@/modules/chat/components/ImagePreview.vue'
import { canCancelUploadMessage } from '@/modules/chat/utils/canCancelUploadMessage'
import store from '@/store'

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
})

const chatStore = useChatStore()
const chatAttachmentsStore = useChatAttachmentsStore(chatStore.chatThread.azure_chat_thread_id)

// Function to determine the component based on attachment type
const attachmentType = computed(() => {
  const icons = {
    [constants.LEARNINGMATERIALS.TYPE_VIDEO]: 'video',
    [constants.LEARNINGMATERIALS.TYPE_AUDIO]: 'audio',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT]: 'unknown',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_WORD]: 'text-document',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_IMAGE]: 'image',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_PDF]: 'pdf',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_POWERPOINT]: 'presentation',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_SHEET]: 'sheet',
    [constants.LEARNINGMATERIALS.TYPE_ZIP]: 'zip',
  }
  return icons[+props.message.metadata.file_type]
})

const size = computed(() => {
  if (!props.message.metadata?.file_size) {
    return 0
  }
  const fileSize = parseFloat(props.message.metadata?.file_size.replace(',', '.'))
  return (fileSize / 1000).toFixed(2)
})

// Check if this image upload can be canceled by the sender
const canCancelImage = computed(() => {
  return canCancelUploadMessage(props.message, store.getters['auth/userId'])
})

async function handleCancelImage() {
  const fileId = chatAttachmentsStore.getFileIdByMessageId(props.message.id)
  if (!fileId) {
    return
  }
  await chatAttachmentsStore.cancelUploadOrDeleteMessage(fileId, props.message.id)
}

const imagePreviewProps = computed(() => {
  const metadata = props.message?.metadata || {}
  const toInt = (v) => {
    const n = parseInt(v, 10)
    return Number.isFinite(n) ? n : undefined
  }
  return {
    src: `${props.message.content}`.startsWith('http') ? props.message.content : undefined,
    width: toInt(metadata.image_width),
    height: toInt(metadata.image_height),
    previewBase64: metadata.preview_base64 || undefined,
    isLoading: metadata?.state === 'sending',
    uploadedPercentage: metadata?.uploadedPercentage,
    canCancel: canCancelImage.value,
  }
})

// Parse audio volume metadata into a numeric [0,1] array
const volumes = computed(() => {
  const raw = props.message.metadata?.audio_volumes
  if (!raw) {
    return null
  }
  return JSON.parse(raw)
})

// Pre-computed audio duration that comes from metadata so we don't have to load the file to know it
const audioDurationSeconds = computed(() => {
  const value = props.message.metadata?.audio_duration_seconds
  if (!value) {
    return 0
  }
  return Number(value)
})

const openImage = () => {
  chatStore.imageToShowUrl = props.message.content
  chatStore.isImageModalOpen = true
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/config';

.s-chat__image {
  border-radius: $spacing-2;
  max-width: 100%;
  cursor: pointer;
  min-height: 250px;
  max-height: 300px;
  object-fit: cover;
}

.s-chat__video {
  border-radius: $spacing-2;
  max-height: 250px;
  max-width: 100%;
  min-height: 250px;
}

.s-chat__audio {
  border-radius: $spacing-2;
  max-width: 100%;
}

.s-chat__messageDownload {
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: $spacing-2;

  .c-icon {
    width: $spacing-6;
    height: $spacing-6;
  }
}

.s-d-block {
  display: block;
}

.s-messageAttachment__fileName {
  white-space: pre-line;
  word-break: break-word;
}
</style>
